import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  offlineDb,
  cacheAreaData,
  getCachedArea,
  isAreaCached,
  clearAreaCache,
  addPendingChange,
  getPendingChanges,
  markChangeSynced,
  clearSyncedChanges,
  updateCachedUnit,
  addCachedNote,
  deleteCachedNote
} from '@/lib/offlineDb';
import type { D2DArea, D2DStreet, D2DHouse, D2DUnit, D2DNote, D2DHouseNote } from '@/types/d2d';

interface UseOfflineCacheProps {
  userId?: string;
  isOnline: boolean;
}

export const useOfflineCache = ({ userId, isOnline }: UseOfflineCacheProps) => {
  const [isCaching, setIsCaching] = useState(false);
  const [cachedAreaIds, setCachedAreaIds] = useState<string[]>([]);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load cached area IDs on mount
  useEffect(() => {
    const loadCachedAreaIds = async () => {
      try {
        const areas = await offlineDb.areas.toArray();
        setCachedAreaIds(areas.map(a => a.id));
      } catch (error) {
        console.error('Error loading cached area IDs:', error);
      }
    };
    loadCachedAreaIds();
  }, []);

  // Sync pending changes when online
  useEffect(() => {
    if (isOnline && userId) {
      syncPendingChanges();
      
      // Set up periodic sync every 5 minutes
      syncIntervalRef.current = setInterval(() => {
        syncPendingChanges();
      }, 5 * 60 * 1000);
    }
    
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isOnline, userId]);

  // Sync pending changes to server
  const syncPendingChanges = useCallback(async () => {
    if (!userId || !isOnline) return;
    
    try {
      const pendingChanges = await getPendingChanges();
      if (pendingChanges.length === 0) return;
      
      console.log(`🔄 Syncing ${pendingChanges.length} pending changes...`);
      
      for (const change of pendingChanges) {
        try {
          switch (change.type) {
            case 'unit_update':
              const { error: updateError } = await supabase
                .from('d2d_units')
                .update(change.payload)
                .eq('id', change.entity_id);
              if (updateError) throw updateError;
              break;
              
            case 'note_add':
              const { error: noteError } = await supabase
                .from('d2d_notes')
                .insert(change.payload);
              if (noteError) throw noteError;
              break;
              
            case 'note_delete':
              const { error: deleteError } = await supabase
                .from('d2d_notes')
                .delete()
                .eq('id', change.entity_id);
              if (deleteError) throw deleteError;
              break;
          }
          
          await markChangeSynced(change.id);
        } catch (error) {
          console.error(`Failed to sync change ${change.id}:`, error);
        }
      }
      
      await clearSyncedChanges();
      console.log('✅ Sync completed');
    } catch (error) {
      console.error('Error syncing pending changes:', error);
    }
  }, [userId, isOnline]);

  // Cache an entire area when activated
  const cacheArea = useCallback(async (areaId: string) => {
    if (!userId) return;
    
    setIsCaching(true);
    try {
      console.log(`📦 Caching area ${areaId}...`);
      
      // Load all streets for the area
      const { data: streets, error: streetsError } = await supabase
        .from('d2d_streets')
        .select('*')
        .eq('area_id', areaId)
        .eq('is_active', true);
      
      if (streetsError) throw streetsError;
      
      const streetIds = (streets || []).map(s => s.id);
      
      let houses: D2DHouse[] = [];
      let units: D2DUnit[] = [];
      let notes: D2DNote[] = [];
      let houseNotes: D2DHouseNote[] = [];
      
      if (streetIds.length > 0) {
        // Load all houses
        const { data: housesData, error: housesError } = await supabase
          .from('d2d_houses')
          .select('*')
          .in('street_id', streetIds);
        
        if (housesError) throw housesError;
        houses = housesData || [];
        
        const houseIds = houses.map(h => h.id);
        
        if (houseIds.length > 0) {
          // Load all units
          const { data: unitsData, error: unitsError } = await supabase
            .from('d2d_units')
            .select('*')
            .in('house_id', houseIds);
          
          if (unitsError) throw unitsError;
          units = unitsData || [];
          
          const unitIds = units.map(u => u.id);
          
          if (unitIds.length > 0) {
            // Load all notes
            const { data: notesData, error: notesError } = await supabase
              .from('d2d_notes')
              .select('*')
              .in('unit_id', unitIds);
            
            if (notesError) throw notesError;
            notes = notesData || [];
          }
          
          // Load house notes
          const { data: houseNotesData, error: houseNotesError } = await supabase
            .from('d2d_house_notes')
            .select('*')
            .in('house_id', houseIds);
          
          if (houseNotesError) throw houseNotesError;
          houseNotes = houseNotesData || [];
        }
      }
      
      // Get area data
      const { data: area, error: areaError } = await supabase
        .from('d2d_areas')
        .select('*')
        .eq('id', areaId)
        .single();
      
      if (areaError) throw areaError;
      
      // Cache everything
      await cacheAreaData(area, streets || [], houses, units, notes, houseNotes);
      
      // Update cached area IDs
      setCachedAreaIds(prev => [...new Set([...prev, areaId])]);
    } catch (error) {
      console.error('Error caching area:', error);
      toast({
        title: "Fehler beim Cachen",
        description: "Gebiet konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setIsCaching(false);
    }
  }, [userId]);

  // Get cached streets for an area
  const getCachedStreets = useCallback(async (areaId: string): Promise<D2DStreet[]> => {
    try {
      const cachedData = await getCachedArea(areaId);
      return cachedData?.streets || [];
    } catch (error) {
      console.error('Error getting cached streets:', error);
      return [];
    }
  }, []);

  // Get cached houses for a street
  const getCachedHouses = useCallback(async (streetId: string): Promise<D2DHouse[]> => {
    try {
      const houses = await offlineDb.houses.where('street_id').equals(streetId).toArray();
      
      // Attach units and notes to houses
      const houseIds = houses.map(h => h.id);
      const units = await offlineDb.units.where('house_id').anyOf(houseIds).toArray();
      const unitIds = units.map(u => u.id);
      const notes = unitIds.length > 0 
        ? await offlineDb.notes.where('unit_id').anyOf(unitIds).toArray()
        : [];
      const houseNotes = await offlineDb.houseNotes.where('house_id').anyOf(houseIds).toArray();
      
      return houses.map(house => {
        const houseUnits = units.filter(u => u.house_id === house.id);
        const processedUnits = houseUnits.map(unit => ({
          ...unit,
          notes: notes.filter(n => n.unit_id === unit.id),
          notes_count: notes.filter(n => n.unit_id === unit.id).length
        }));
        
        return {
          ...house,
          units: processedUnits,
          notes: houseNotes.filter(n => n.house_id === house.id),
          notes_count: houseNotes.filter(n => n.house_id === house.id).length
        };
      });
    } catch (error) {
      console.error('Error getting cached houses:', error);
      return [];
    }
  }, []);

  // Get cached units for a house
  const getCachedUnits = useCallback(async (houseId: string): Promise<D2DUnit[]> => {
    try {
      const units = await offlineDb.units.where('house_id').equals(houseId).toArray();
      const unitIds = units.map(u => u.id);
      const notes = unitIds.length > 0
        ? await offlineDb.notes.where('unit_id').anyOf(unitIds).toArray()
        : [];
      
      return units.map(unit => ({
        ...unit,
        notes: notes.filter(n => n.unit_id === unit.id),
        notes_count: notes.filter(n => n.unit_id === unit.id).length
      }));
    } catch (error) {
      console.error('Error getting cached units:', error);
      return [];
    }
  }, []);

  // Update unit offline (cache + pending change)
  const updateUnitOffline = useCallback(async (unitId: string, updates: Partial<D2DUnit>) => {
    try {
      // Update local cache
      await updateCachedUnit(unitId, updates);
      
      // Add pending change for sync
      await addPendingChange({
        type: 'unit_update',
        entity_id: unitId,
        payload: updates
      });
      
      return true;
    } catch (error) {
      console.error('Error updating unit offline:', error);
      return false;
    }
  }, []);

  // Add note offline
  const addNoteOffline = useCallback(async (note: D2DNote) => {
    try {
      await addCachedNote(note);
      await addPendingChange({
        type: 'note_add',
        entity_id: note.id,
        payload: note
      });
      return true;
    } catch (error) {
      console.error('Error adding note offline:', error);
      return false;
    }
  }, []);

  // Delete note offline
  const deleteNoteOffline = useCallback(async (noteId: string) => {
    try {
      await deleteCachedNote(noteId);
      await addPendingChange({
        type: 'note_delete',
        entity_id: noteId,
        payload: {}
      });
      return true;
    } catch (error) {
      console.error('Error deleting note offline:', error);
      return false;
    }
  }, []);

  // Check if area is cached
  const checkAreaCached = useCallback(async (areaId: string): Promise<boolean> => {
    return await isAreaCached(areaId);
  }, []);

  // Get all cached units for an area (for lead count cards)
  const getCachedAreaUnits = useCallback(async (areaId: string): Promise<D2DUnit[]> => {
    try {
      const cachedData = await getCachedArea(areaId);
      if (!cachedData) return [];
      
      // Attach notes to units
      const unitsWithNotes = cachedData.units.map(unit => ({
        ...unit,
        notes: cachedData.notes.filter(n => n.unit_id === unit.id),
        notes_count: cachedData.notes.filter(n => n.unit_id === unit.id).length
      }));
      
      return unitsWithNotes;
    } catch (error) {
      console.error('Error getting cached area units:', error);
      return [];
    }
  }, []);

  // Clear cache for a specific area
  const clearCache = useCallback(async (areaId: string) => {
    try {
      await clearAreaCache(areaId);
      setCachedAreaIds(prev => prev.filter(id => id !== areaId));
      console.log(`🗑️ Cleared cache for area: ${areaId}`);
    } catch (error) {
      console.error('Error clearing area cache:', error);
    }
  }, []);

  return {
    isCaching,
    cachedAreaIds,
    cacheArea,
    clearCache,
    getCachedStreets,
    getCachedHouses,
    getCachedUnits,
    getCachedAreaUnits,
    updateUnitOffline,
    addNoteOffline,
    deleteNoteOffline,
    checkAreaCached,
    syncPendingChanges
  };
};
